import React, { useState, useEffect } from "react";
import {
  Backdrop,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import { useSelector } from "react-redux";
import CoreButtonIcon from "../../../app/core/components/CoreButtonIcon/CoreButtonIcon";
import { toast } from "react-toastify";
import { useGetUsersQuery } from "../../api/admin/gestisciUtentiApi";
import {
  useAssociaUserMutation,
  useDissociaUserMutation,
} from "../../api/admin/gestisciCommercialistiApi";
import UserFilter from "../../../app/core/components/UserFilter/UserFilter";
import CoreDialog from "../../../app/core/components/CoreDialog/CoreDialog";
import { t } from "i18next";

const AssociateTableUser = ({ operatorData, isLoadingCommercialisti }) => {
  const [userAssociate, setUserAssociate] = useState(operatorData?.Data);
  const [selectedUser, setSelectedUser] = useState("");
  const [debouncedInputUserValue, setDebouncedInputUserValue] = useState("");
  const [userToDisassociate, setUserToDisassociate] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const colorPrimary = useSelector((state) => state.app.primaryColor);

  const {
    data,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    refetch: userRefetch,
  } = useGetUsersQuery({
    page: { startRow: 0, endRow: 50 },
    value: debouncedInputUserValue,
  });
  const [associaUser, { isLoading: isAssociatedLoading }] =
    useAssociaUserMutation();
  const [dissociaUser, { isLoading: isDisassociatedLoading }] =
    useDissociaUserMutation();

  const isLoading =
    isAssociatedLoading ||
    isDisassociatedLoading ||
    isUserLoading ||
    isUserFetching;

  const handleAssociateUser = (el) => {
    const isAlreadyAssociated = userAssociate.associatedUsers.some(
      (associatedUser) => associatedUser.id === el.id
    );
    if (isAlreadyAssociated) {
      toast.warn(`${t("UserAlreadyAssociated")}`);
    } else {
      const associateData = {
        adminId: userAssociate.id,
        userId: el.id,
        userEmail: el.email,
      };
      associaUser(associateData).then((res) => {
        if (res.data.Result === 1) {
          toast.success(t("UserAssociated"));
        } else {
          toast.error(res.data.Exception);
        }
      });
    }
  };

  const handleDisassociateUser = (user) => {
    setUserToDisassociate(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setUserToDisassociate();
  };

  const confirmDisassociateUser = () => {
    const disassociateData = {
      adminId: userToDisassociate.adminId,
      userId: userToDisassociate.id,
      userEmail: userToDisassociate.email,
    };
    dissociaUser(disassociateData)
      .then((res) => {
        if (res.data.Result === 1) {
          handleCloseDialog();
          setOpenDialog(false);
          userRefetch(); //usiamo il refetch perché le chiamate sono su due file slice diversi e gli invalid tags non funzionerebbero :(
          toast.success(t("UserUnassociated"));
        } else {
          toast.error(res.data.Exception);
        }
      })
      .catch(() => toast.error(t("ReqErr")));
  };

  useEffect(() => {
    setUserAssociate(operatorData?.Data);
  }, [operatorData?.Data]);

  return (
    <div>
      <h3 className="flex">
        {t("UsersAssociated")}
        <div
          className="w-6 h-6 mx-4 rounded-3xl"
          style={{ backgroundColor: `${colorPrimary}` }}
        >
          <small className="text-white flex justify-center">
            {userAssociate?.associatedUsers?.length}
          </small>
        </div>
      </h3>

      <div className="py-6 flex space-x-2">
        <div className="w-full">
          {data && data?.Data && (
            <UserFilter
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              usersList={data?.Data?.items || []}
              setDebouncedInputUserValue={setDebouncedInputUserValue}
              isUsersLoading={isUserLoading || isUserFetching}
            />
          )}
        </div>
        <CoreButtonIcon
          className={!selectedUser ? "text-gray-600" : "text-blue-600  "}
          icon={
            <PersonAddAltRoundedIcon
              fontSize="small"
              className={!selectedUser ? "text-gray-600" : "text-blue-600  "}
            />
          }
          testo={t("Associate")}
          onClick={() => {
            handleAssociateUser(selectedUser);
            setSelectedUser("");
          }}
          disabled={
            !selectedUser || isAssociatedLoading || isLoadingCommercialisti
          }
        />
      </div>

      <TableContainer
        sx={{ position: "relative" }}
        component={Paper}
        elevation={4}
      >
        <Backdrop
          className="backdrop-animation"
          sx={{ position: "absolute", zIndex: 1 }}
          open={isLoading}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("Name")}</TableCell>
              <TableCell>{t("Surname")}</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          {userAssociate?.associatedUsers?.map((el, id) => (
            <TableRow key={id}>
              <TableCell>{el.name}</TableCell>
              <TableCell>{el.surname}</TableCell>
              <TableCell>{el.email}</TableCell>
              <TableCell>
                <IconButton
                  key={id}
                  variant="outlined"
                  onClick={() => handleDisassociateUser(el)}
                  disabled={isDisassociatedLoading}
                >
                  <HighlightOffRoundedIcon
                    fontSize="small"
                    className="text-rose-700"
                  />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </TableContainer>
      <CoreDialog
        open={openDialog}
        title={t("UnassociateUser")}
        content={`${t("UserConfirmUnassociation")} ${
          userToDisassociate?.name
        } ${userToDisassociate?.surname} ${t("fromYourStudio")}?`}
        negativeButton={t("No")}
        positiveButton={t("Yes")}
        handlePositive={confirmDisassociateUser}
        closeDialog={handleCloseDialog}
        disabledButton={isDisassociatedLoading}
      />
    </div>
  );
};

export default AssociateTableUser;
